import React, { Component } from 'react';
import { NumericInput } from '..';
import { withInjectedProps } from '../InjectedPropsContext';
import { TextInputBase } from '../TextInput';
import { ChangeEventWithData } from '../types';
import RadioInput from './RadioInput';

interface RadioGroupOption<TValue = any> {
  label?: string;
  value?: TValue;
  disabled?: boolean;
}

/**
 * An alternative to dropdowns for displaying a list of options.
 * This implementation follows the dropdown component. Try to keep them consistent for ease of use.
 */
export interface IRadioGroupProps<TValue = any> {
  defaultValue?: TValue;
  disabled?: boolean;
  freeEntry?: boolean;
  freeEntryLabel?: React.ReactNode;
  freeEntryMaxLength?: number;
  name?: string;
  onChange?: (event: ChangeEventWithData<HTMLInputElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  options?: RadioGroupOption<TValue>[];
  value?: TValue;
  freeEntryInput?: typeof TextInputBase | typeof NumericInput;
}

class RadioGroupBase extends Component<IRadioGroupProps, any> {
  static idCounter = 0;

  constructor(props: IRadioGroupProps) {
    super(props);

    this.state = {
      radioGroupId: RadioGroupBase.idCounter++,
      value: props.defaultValue,
      freeEntryValue: null,
    };
  }

  render() {
    const {
      disabled,
      freeEntry = false,
      freeEntryLabel,
      freeEntryMaxLength,
      name,
      onChange,
      options = [],
      value: valueProp,
      freeEntryInput: FreeEntryInput = TextInputBase,
    } = this.props;

    // Radio group needs a unique name
    const groupName = name ?? `radioGroup-${this.state.radioGroupId}`;

    // Radio input names need to be unique between all radio group instances, but the same for all inputs in a group.
    const inputName = name ? `${name}-${this.state.radioGroupId}` : groupName;

    const isControlled = valueProp !== undefined;
    const selectedValue = isControlled ? valueProp : this.state.value;

    const freeEntrySelected = options.reduce((acc, option) => acc && option.value !== selectedValue, true);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!disabled) {
        // Use the component name, not the input name, because it will have the id postfixed.
        (event as any).data = { name, value: (event as any).data.value, submitValues: true };
        onChange?.(event as any);

        if (!isControlled) this.setState({ value: (event as any).data.value });
        if (!freeEntrySelected) this.setState({ freeEntryValue: '' });
      }
    };

    return (
      <div className="RadioGroup">
        {options &&
          options.map((option, i) => (
            <RadioInput
              checked={option.value === undefined ? undefined : option.value === selectedValue}
              disabled={disabled || option.disabled}
              key={i}
              name={inputName}
              onChange={handleChange}
              value={option.value}
            >
              {option.label}
            </RadioInput>
          ))}

        {freeEntry && (
          <RadioInput
            checked={freeEntrySelected}
            disabled={disabled}
            name={inputName}
            onChange={handleChange}
            value={freeEntrySelected ? selectedValue : null}
          >
            {freeEntryLabel}
          </RadioInput>
        )}

        {freeEntry && freeEntrySelected && (
          <FreeEntryInput
            {...{
              name,
              disabled,
              value: this.state.freeEntryValue ?? valueProp ?? '',
              maxLength: freeEntryMaxLength,
              onChange: (event) => {
                this.setState({ freeEntryValue: event.target.value });
              },
              onBlur: handleChange,
              submitValues: false,
            }}
          />
        )}
      </div>
    );
  }
}

const RadioGroup = withInjectedProps(RadioGroupBase);

export { RadioGroup, RadioGroupBase };
export default RadioGroup;
